import axiosInstance from '../axiosInstance';
import { notifyCartChanged } from '../../utils/cartStatus';

/**
 * 장바구니 조회
 * @param {object} [options]
 * @param {boolean} [options.skipLoading] - true면 전역 로딩 오버레이를 띄우지 않는다.
 *   GNB의 백그라운드 폴링처럼 사용자가 의도하지 않은 호출에 사용.
 */
export const getCartItems = async ({ skipLoading = false } = {}) => {
  try {
    const response = await axiosInstance.get('/cart-items', { skipLoading });
    return response.data.data;
  } catch (error) {
    console.error('Get Cart Items API Error:', error);
    throw error;
  }
};

/**
 * 장바구니에 추가
 * @param {number} workshopDateSlotId - 워크샵 날짜 슬롯 ID
 * @param {number} quantity - 수량 (기본값: 1)
 * @param {object} [options]
 * @param {boolean} [options.skipLoading] - true면 전역 로딩 오버레이를 띄우지 않는다.
 *   모달 내부 동작처럼 인라인 로딩으로 처리할 때 사용.
 * @param {boolean} [options.skipCartStatusSync] - true면 장바구니 상태 동기화 이벤트를 발생시키지 않는다.
 *   waitlist 등록처럼 곧바로 cart item이 정리되는 흐름에서 사용한다.
 */
export const addToCart = async (
  workshopDateSlotId,
  quantity = 1,
  { skipLoading = false, skipCartStatusSync = false } = {},
) => {
  try {
    const response = await axiosInstance.post(
      '/cart-items',
      {
        workshopDateSlotId,
        quantity,
      },
      { skipLoading },
    );
    if (!skipCartStatusSync) notifyCartChanged();
    return response.data.data;
  } catch (error) {
    console.error('Add to Cart API Error:', error);
    throw error;
  }
};

/**
 * 장바구니 아이템 삭제
 * @param {number} cartItemId - 장바구니 아이템 ID
 */
export const removeFromCart = async (cartItemId) => {
  try {
    const response = await axiosInstance.delete(`/cart-items/${cartItemId}`);
    notifyCartChanged();
    return response.data.data;
  } catch (error) {
    console.error('Remove from Cart API Error:', error);
    throw error;
  }
};

/**
 * 장바구니 아이템 선택 상태 변경
 * @param {number} cartItemId - 장바구니 아이템 ID
 * @param {boolean} isSelected - 선택 상태
 */
export const updateCartItemSelection = async (cartItemId, isSelected) => {
  try {
    const response = await axiosInstance.patch(`/cart-items/${cartItemId}`, {
      isSelected,
    });
    return response.data.data;
  } catch (error) {
    console.error('Update Cart Item Selection API Error:', error);
    throw error;
  }
};

/**
 * 장바구니 아이템 수량 변경
 * @param {number} cartItemId - 장바구니 아이템 ID
 * @param {number} quantity - 수량
 */
export const updateCartItemQuantity = async (cartItemId, quantity) => {
  try {
    const response = await axiosInstance.patch(`/cart-items/${cartItemId}`, {
      quantity,
    });
    return response.data.data;
  } catch (error) {
    console.error('Update Cart Item Quantity API Error:', error);
    throw error;
  }
};

/**
 * 장바구니 아이템 날짜 슬롯 변경
 * @param {number} cartItemId - 장바구니 아이템 ID
 * @param {number} workshopDateSlotId - 새로운 워크샵 날짜 슬롯 ID
 */
export const updateCartItemDateSlot = async (cartItemId, workshopDateSlotId) => {
  try {
    const response = await axiosInstance.patch(`/cart-items/${cartItemId}`, {
      workshopDateSlotId,
    });
    return response.data.data;
  } catch (error) {
    console.error('Update Cart Item Date Slot API Error:', error);
    throw error;
  }
};

/**
 * 여러 장바구니 아이템 일괄 삭제
 * @param {number[]} cartItemIds - 장바구니 아이템 ID 배열
 */
export const removeMultipleFromCart = async (cartItemIds) => {
  try {
    const response = await axiosInstance.delete('/cart-items', {
      data: { cartItemIds },
    });
    notifyCartChanged();
    return response.data.data;
  } catch (error) {
    console.error('Remove Multiple from Cart API Error:', error);
    throw error;
  }
};

